import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { allRecommendations } from '../data/aiRecommendationsData';
import { usePermissions } from '../hooks/usePermissions';
import { useApp } from './AppContext';
import { DEMO_USERS } from '@/contexts/UsersContext';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ReviewedItem {
  id: string;
  reviewedAt: Date;
  reviewedByPersona: string; // swap for userId when wiring to live API
}

export interface InsightComment {
  id: string;
  insightId: string;
  authorId: string;        // Persona ID — swap for userId in live mode
  authorName: string;      // Display name
  authorRole: string;      // Role label
  text: string;
  createdAt: Date;
}

interface RecommendationsContextType {
  // Reviewed state — lifted here so the nav badge and page share the same source
  reviewedItems: ReviewedItem[];
  markReviewed: (id: string) => void;
  reinstate: (id: string) => void;

  // Discussion — per-insight comment threads
  insightComments: InsightComment[];
  addInsightComment: (insightId: string, text: string) => void;
  getInsightComments: (insightId: string) => InsightComment[];
  getInsightCommentCount: (insightId: string) => number;

  // Nav badge — count of open (not reviewed) insights the current user can see
  // Respects permissions so Sarah only sees her permitted count
  openInsightCount: number;
}

// ── Context ───────────────────────────────────────────────────────────────────

const RecommendationsContext = createContext<RecommendationsContextType | undefined>(undefined);

// ── Seed comments ─────────────────────────────────────────────────────────────
// Demonstrates discussion threads on two insights from the start.
// Mirrors the ActionsContext seed pattern — realistic BuildRight scenario.

const today = new Date();
today.setHours(0, 0, 0, 0);

const hoursAgo = (n: number) => new Date(today.getTime() - n * 60 * 60 * 1000);
const daysAgo  = (n: number) => new Date(today.getTime() - n * 24 * 60 * 60 * 1000);

const SEED_INSIGHT_COMMENTS: InsightComment[] = [
  // REC-001 — Cash Concentration Risk: three-way discussion
  {
    id: 'ic-001-1',
    insightId: 'REC-001',
    authorId: '4',
    authorName: 'Linda Kowalski',
    authorRole: 'CFO',
    text: 'This one needs immediate attention. Education Facilities Ltd alone is 41% of our 60-day inflow — if they push back the milestone sign-off we\'re in trouble. Sarah, can you get on a call with their PM this week?',
    createdAt: daysAgo(2),
  },
  {
    id: 'ic-001-2',
    insightId: 'REC-001',
    authorId: '6',
    authorName: 'Sarah Mitchell',
    authorRole: 'Finance Manager',
    text: 'Agreed. I spoke to their PM yesterday — they\'re expecting sign-off by end of this week, which would release INV-1262 and INV-1254. I\'ve asked Edna to send a payment reminder to Local Government Authority too as their 8-day average lateness is a known pattern.',
    createdAt: daysAgo(1),
  },
  {
    id: 'ic-001-3',
    insightId: 'REC-001',
    authorId: '4',
    authorName: 'Linda Kowalski',
    authorRole: 'CFO',
    text: 'Good. Let\'s also make sure the overdraft facility is confirmed available before month end as a contingency. I\'ll cover that with HSBC directly.',
    createdAt: hoursAgo(4),
  },

  // REC-003 — Local Government Payment Pattern: two-way discussion
  {
    id: 'ic-003-1',
    insightId: 'REC-003',
    authorId: '6',
    authorName: 'Sarah Mitchell',
    authorRole: 'Finance Manager',
    text: 'We see this every quarter with LGA — their internal approvals take 8–10 days longer than the invoice terms. Worth building a note into the forecast model as a standing assumption rather than treating it as a surprise each time.',
    createdAt: daysAgo(3),
  },
  {
    id: 'ic-003-2',
    insightId: 'REC-003',
    authorId: '5',
    authorName: 'Marcus Patel',
    authorRole: 'Regional Manager',
    text: 'Agreed — and worth raising in the next contract review. If we can get them to commit to 21-day terms in writing rather than 14-day that\'s a more honest baseline. Can you draft a note to include in the renewal discussion?',
    createdAt: daysAgo(1),
  },
];

// ── Provider ──────────────────────────────────────────────────────────────────

export const RecommendationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useApp();
  const { hasPermission } = usePermissions();

  const [reviewedItems, setReviewedItems]       = useState<ReviewedItem[]>([]);
  const [insightComments, setInsightComments]   = useState<InsightComment[]>(SEED_INSIGHT_COMMENTS);

  const markReviewed = useCallback((id: string) => {
    setReviewedItems(prev => [
      ...prev,
      { id, reviewedAt: new Date(), reviewedByPersona: currentUser.id },
    ]);
  }, [currentUser.id]);

  const reinstate = useCallback((id: string) => {
    setReviewedItems(prev => prev.filter(r => r.id !== id));
  }, []);

  const addInsightComment = useCallback((insightId: string, text: string) => {
    const user = DEMO_USERS.find(u => u.id === currentUser.id) ?? currentUser;
    const comment: InsightComment = {
      id: `ic-${Date.now()}`,
      insightId,
      authorId: currentUser.id,
      authorName: user.name,
      authorRole: user.forecasterRole,
      text,
      createdAt: new Date(),
    };
    setInsightComments(prev => [...prev, comment]);
  }, [currentUser]);

  const getInsightComments = useCallback((insightId: string) => {
    return insightComments.filter(c => c.insightId === insightId);
  }, [insightComments]);

  const getInsightCommentCount = useCallback((insightId: string) => {
    return insightComments.filter(c => c.insightId === insightId).length;
  }, [insightComments]);

  // Compute the open count — only permissions gate matters here (no UI filters,
  // because the badge should reflect reality not the current filter selection)
  const openInsightCount = useMemo(() => {
    const canViewOperational = hasPermission('view_operational_insights');
    const canViewStrategic   = hasPermission('view_strategic_insights');
    const reviewedIds = new Set(reviewedItems.map(r => r.id));

    return allRecommendations.filter(rec => {
      if (reviewedIds.has(rec.id)) return false;
      if (rec.category === 'OPERATIONAL' && !canViewOperational) return false;
      if (rec.category === 'STRATEGIC'   && !canViewStrategic)   return false;
      return true;
    }).length;
  }, [reviewedItems, hasPermission]);

  return (
    <RecommendationsContext.Provider value={{
      reviewedItems,
      markReviewed,
      reinstate,
      insightComments,
      addInsightComment,
      getInsightComments,
      getInsightCommentCount,
      openInsightCount,
    }}>
      {children}
    </RecommendationsContext.Provider>
  );
};

// ── Hook ──────────────────────────────────────────────────────────────────────

export const useRecommendations = () => {
  const context = useContext(RecommendationsContext);
  if (!context) throw new Error('useRecommendations must be used within a RecommendationsProvider');
  return context;
};
